// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { cn } from '@/lib/utils'
import { Spinner } from '@/components/ui'

interface LoadingStateProps {
  message?:   string
  className?: string
  size?:      'sm' | 'md' | 'lg'
}

export function LoadingState({ message = 'Laden...', className, size = 'md' }: LoadingStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16', className)}>
      <Spinner size={size} />
      <p className="mt-3 text-sm text-ink-3">{message}</p>
    </div>
  )
}

// Skeleton components for layout-preserving loading
export function SkeletonLine({ width = 'full', height = 'md' }: { width?: string; height?: string }) {
  const widths: Record<string, string> = {
    full: 'w-full', '3/4': 'w-3/4', '1/2': 'w-1/2', '1/3': 'w-1/3',
  }
  const heights: Record<string, string> = {
    sm: 'h-3', md: 'h-4', lg: 'h-5', xl: 'h-7',
  }
  return (
    <div
      className={cn(
        'rounded-lg bg-sand-200 animate-pulse',
        widths[width] ?? width,
        heights[height] ?? height,
      )}
    />
  )
}

export function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-sand-300 bg-white p-5 flex flex-col gap-3">
      <SkeletonLine height="lg" width="1/2" />
      <SkeletonLine />
      <SkeletonLine width="3/4" />
    </div>
  )
}

export function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  )
}
